
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BarChart3, 
  Link as LinkIcon, 
  Home,
  ChevronLeft,
  ChevronRight,
  Menu,
  Settings,
  Users,
  Shield,
  UserCheck
} from 'lucide-react';
import { 
  Sidebar, 
  SidebarContent, 
  SidebarFooter, 
  SidebarHeader, 
  SidebarMenu, 
  SidebarMenuButton, 
  SidebarMenuItem, 
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  useSidebar
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { useAffiliateInfo } from '@/hooks/useAffiliate';

const menuItems = [
  {
    name: 'Dashboard',
    path: '/affiliate',
    icon: Home
  },
  {
    name: 'Referral Links',
    path: '/affiliate/links',
    icon: LinkIcon
  },
  // Removed Reports & Analytics menu item
  {
    name: 'Referred Users',
    path: '/affiliate/referrals',
    icon: Users
  },
  {
    name: 'Manage User Limits',
    path: '/affiliate/manage-limits',
    icon: UserCheck
  },
  // Removed Resources menu item
  {
    name: 'Settings',
    path: '/affiliate/settings',
    icon: Settings
  }
];

export const AffiliateSidebar = () => {
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { user } = useAuth();
  const { t } = useTranslation();
  const { data: affiliateInfo } = useAffiliateInfo();
  
  // Check if user has admin permission based on role in user metadata
  const isAdmin = user && ['admin', 'superadmin'].includes(user.user_metadata?.role || '');
  
  return (
    <>
      {isCollapsed && (
        <Button 
          onClick={toggleSidebar} 
          className="fixed left-3 top-3 z-20 h-8 w-8 rounded-full p-0 shadow-md" 
          size="icon" 
          variant="outline"
        >
          <Menu size={16} />
        </Button>
      )}
      
      <Sidebar className="border-r border-border bg-card">
        <SidebarHeader style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Users size={18} className="text-primary" />
              </div>
              {!isCollapsed && (
                <div>
                  <h2 className="text-lg font-semibold">Affiliate Program</h2>
                  <p className="text-xs text-muted-foreground">
                    {affiliateInfo?.status === 'active' 
                      ? 'Active Partner' 
                      : 'Application Pending'}
                  </p>
                </div>
              )}
            </div>
            <button 
              onClick={toggleSidebar} 
              className={cn(
                "p-1.5 rounded-full hover:bg-sidebar-accent transition-colors", 
                !isCollapsed ? "" : "hidden"
              )}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </button>
          </div>
        </SidebarHeader>
        
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel className="text-sm font-semibold text-muted-foreground">
              Navigation
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {menuItems.map((item) => (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton 
                      asChild 
                      isActive={location.pathname === item.path} 
                      tooltip={isCollapsed ? item.name : undefined}
                    >
                      <Link 
                        to={item.path} 
                        className={cn(
                          "relative", 
                          location.pathname === item.path && 
                          "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-8 before:w-1 before:bg-primary before:rounded-r-full"
                        )}
                      >
                        <item.icon className={cn(
                          "text-muted-foreground transition-colors", 
                          location.pathname === item.path && "text-primary"
                        )} />
                        <span className={cn(
                          "text-base font-medium text-muted-foreground transition-colors", 
                          location.pathname === item.path && "text-foreground"
                        )}>
                          {item.name}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
          
          {/* Removed the Administration section with Admin Panel link */}
          
          <SidebarGroup>
            <SidebarGroupLabel className="text-sm font-semibold text-muted-foreground">
              Account
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton 
                    asChild 
                    isActive={location.pathname === '/affiliate/settings'} 
                    tooltip={isCollapsed ? "Settings" : undefined}
                  >
                    <Link 
                      to="/affiliate/settings" 
                      className={cn(
                        "relative", 
                        location.pathname === '/affiliate/settings' && 
                        "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-8 before:w-1 before:bg-primary before:rounded-r-full"
                      )}
                    >
                      <Settings className={cn(
                        "text-muted-foreground transition-colors", 
                        location.pathname === '/affiliate/settings' && "text-primary"
                      )} />
                      <span className={cn(
                        "text-base font-medium text-muted-foreground transition-colors", 
                        location.pathname === '/affiliate/settings' && "text-foreground"
                      )}>
                        Settings
                      </span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <div className={cn("p-4 border-t border-sidebar-border", isCollapsed ? "flex justify-center" : "space-y-4")}>
            <Link to="/" className="flex items-center gap-2 text-sm text-primary hover:underline">
              <Home size={16} />
              {!isCollapsed && <span>Back to Main App</span>}
            </Link>
          </div>
        </SidebarFooter>
      </Sidebar>
    </>
  );
};
